import { collection, addDoc, query, where, orderBy, getDocs, updateDoc, doc, serverTimestamp, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  NOTIFICATION_TYPES,
  NOTIFICATION_STATUS,
  NOTIFICATION_TYPE_TRANSLATIONS,
  createNotification
} from '../models/Notification';
import { PROBLEM_TRANSLATIONS, BUILDING_TRANSLATIONS } from '../models/WorkRequest';

/**
 * Extrait les infos de localisation d'une demande.
 * Gère deux formats :
 *  - Objet  : { building, floor, room }  (WorkRequestForm.js)
 *  - Chaîne : "bat A"                    (NewWorkRequestForm.js)
 */
function extractLocation(workRequest) {
  const loc = workRequest.location;
  if (loc && typeof loc === 'object') {
    const building = BUILDING_TRANSLATIONS[loc.building] || loc.building || null;
    const room = loc.room || null;
    const floor = loc.floor || null;
    return { building, room, floor };
  }
  // location est une chaîne libre (ex: "bat A" ou "Salle 12")
  if (loc && typeof loc === 'string' && loc.trim()) {
    return { building: loc.trim(), room: null, floor: null };
  }
  return { building: null, room: null, floor: null };
}

/**
 * Extrait le libellé du problème/catégorie d'une demande.
 * Gère problem (clé enum) et category (texte libre).
 */
function extractProblemLabel(workRequest) {
  return PROBLEM_TRANSLATIONS[workRequest.problem]
    || workRequest.problem
    || workRequest.category
    || 'Demande de travaux';
}

export const NotificationService = {
  // Créer une notification pour une nouvelle demande
  async createNewRequestNotification(workRequest) {
    try {
      if (!workRequest.id || !workRequest.assignedServiceId) {
        throw new Error('Informations manquantes pour la création de la notification');
      }

      const problemTranslation = extractProblemLabel(workRequest);
      const { building: buildingTranslation, room: roomValue } = extractLocation(workRequest);
      const promises = [];

      // Notification pour le service assigné
      const assignedServiceNotification = createNotification({
        type: NOTIFICATION_TYPES.NEW_REQUEST,
        workRequestId: workRequest.id,
        serviceId: workRequest.assignedServiceId,
        message: `${NOTIFICATION_TYPE_TRANSLATIONS[NOTIFICATION_TYPES.NEW_REQUEST]} : ${problemTranslation}`,
        details: {
          problem: problemTranslation,
          building: buildingTranslation || null,
          room: roomValue || null,
          priority: workRequest.priority || null,
          title: workRequest.title || null
        }
      });

      promises.push(addDoc(collection(db, 'notifications'), {
        ...assignedServiceNotification,
        createdAt: serverTimestamp()
      }));

      // Notification pour le service demandeur si différent
      if (workRequest.requestingServiceId && workRequest.requestingServiceId !== workRequest.assignedServiceId) {
        const requestingServiceNotification = createNotification({
          type: NOTIFICATION_TYPES.NEW_REQUEST,
          workRequestId: workRequest.id,
          serviceId: workRequest.requestingServiceId,
          message: `Votre demande de travaux a été créée : ${problemTranslation}`,
          details: {
            problem: problemTranslation,
            building: buildingTranslation || null,
            room: roomValue || null,
            priority: workRequest.priority || null,
            title: workRequest.title || null,
            assignedTo: workRequest.assignedServiceId
          }
        });

        promises.push(addDoc(collection(db, 'notifications'), {
          ...requestingServiceNotification,
          createdAt: serverTimestamp()
        }));
      }

      const results = await Promise.all(promises);
      return results[0];
    } catch (error) {
      console.error('Erreur lors de la création de la notification:', error);
      throw error;
    }
  },

  // Créer une notification pour un changement de statut
  async createStatusChangeNotification(workRequest, oldStatus, newStatus) {
    try {
      if (!workRequest.id || !workRequest.assignedServiceId || !oldStatus || !newStatus) {
        throw new Error('Informations manquantes pour la création de la notification de changement de statut');
      }

      const { building: buildingTranslation, room: roomValue } = extractLocation(workRequest);
      const promises = [];

      // Notification pour le service assigné
      const assignedServiceNotification = createNotification({
        type: NOTIFICATION_TYPES.STATUS_CHANGE,
        workRequestId: workRequest.id,
        serviceId: workRequest.assignedServiceId,
        message: `${NOTIFICATION_TYPE_TRANSLATIONS[NOTIFICATION_TYPES.STATUS_CHANGE]} : ${oldStatus} → ${newStatus}`,
        details: {
          oldStatus,
          newStatus,
          building: buildingTranslation || null,
          room: roomValue || null,
          title: workRequest.title || null
        }
      });

      promises.push(addDoc(collection(db, 'notifications'), {
        ...assignedServiceNotification,
        createdAt: serverTimestamp()
      }));

      // Notification pour le demandeur (service ou utilisateur individuel)
      const requesterId = workRequest.requestingServiceId || workRequest.createdBy;
      if (requesterId && requesterId !== workRequest.assignedServiceId) {
        const requesterNotification = createNotification({
          type: NOTIFICATION_TYPES.STATUS_CHANGE,
          workRequestId: workRequest.id,
          serviceId: requesterId,
          message: `Le statut de votre demande a changé : ${oldStatus} → ${newStatus}`,
          details: {
            oldStatus,
            newStatus,
            building: buildingTranslation || null,
            room: roomValue || null,
            title: workRequest.title || null
          }
        });

        promises.push(addDoc(collection(db, 'notifications'), {
          ...requesterNotification,
          createdAt: serverTimestamp()
        }));
      }

      const results = await Promise.all(promises);
      return results[0];
    } catch (error) {
      console.error('Erreur lors de la création de la notification de changement de statut:', error);
      throw error;
    }
  },

  // Récupérer les notifications non lues pour un service — filtrées côté serveur
  async getUnreadNotifications(serviceId) {
    try {
      if (!serviceId) return [];

      const q = query(
        collection(db, 'notifications'),
        where('serviceId', '==', serviceId),
        where('status', '==', NOTIFICATION_STATUS.UNREAD),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('NotificationService: Erreur lors de la récupération des notifications:', error);
      return [];
    }
  },

  // Marquer une notification comme lue
  async markAsRead(notificationId) {
    if (!notificationId) return;

    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        status: NOTIFICATION_STATUS.READ,
        readAt: new Date(),
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('NotificationService: Erreur lors du marquage comme lu:', error);
    }
  },

  // Créer une notification pour un nouveau commentaire
  async createCommentNotification(workRequest, comment, authorId) {
    try {
      if (!workRequest.id || !comment) {
        throw new Error('Informations manquantes pour la création de la notification de commentaire');
      }

      const promises = [];
      
      // Notification pour le demandeur si le commentaire vient du service assigné
      if (workRequest.createdBy && authorId !== workRequest.createdBy) {
        const requesterNotification = createNotification({
          type: NOTIFICATION_TYPES.COMMENT_ADDED,
          workRequestId: workRequest.id,
          serviceId: workRequest.createdBy,
          message: `Nouveau commentaire sur votre demande: ${workRequest.title || workRequest.id.substring(0, 8)}`,
          details: {
            commentText: comment.substring(0, 100) + (comment.length > 100 ? '...' : ''),
            authorId,
            requestTitle: workRequest.title || `Demande #${workRequest.id.substring(0, 8)}`
          }
        });

        promises.push(addDoc(collection(db, 'notifications'), {
          ...requesterNotification,
          createdAt: serverTimestamp()
        }));
      }

      // Notification pour le service assigné si le commentaire vient du demandeur
      if (workRequest.assignedServiceId && authorId !== workRequest.assignedServiceId) {
        const serviceNotification = createNotification({
          type: NOTIFICATION_TYPES.COMMENT_ADDED,
          workRequestId: workRequest.id,
          serviceId: workRequest.assignedServiceId,
          message: `Nouveau commentaire du demandeur: ${workRequest.title || workRequest.id.substring(0, 8)}`,
          details: {
            commentText: comment.substring(0, 100) + (comment.length > 100 ? '...' : ''),
            authorId,
            requestTitle: workRequest.title || `Demande #${workRequest.id.substring(0, 8)}`
          }
        });

        promises.push(addDoc(collection(db, 'notifications'), {
          ...serviceNotification,
          createdAt: serverTimestamp()
        }));
      }

      if (promises.length > 0) {
        const results = await Promise.all(promises);
        return results[0];
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la création de la notification de commentaire:', error);
      throw error;
    }
  },

  // Créer une notification pour une intervention planifiée
  async createScheduledInterventionNotification(workRequest, interventionDate) {
    try {
      if (!workRequest.id || !workRequest.createdBy || !interventionDate) {
        throw new Error('Informations manquantes pour la notification d\'intervention');
      }

      const formattedDate = new Date(interventionDate).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      const requesterNotification = createNotification({
        type: NOTIFICATION_TYPES.SCHEDULED_INTERVENTION,
        workRequestId: workRequest.id,
        serviceId: workRequest.createdBy,
        message: `Intervention planifiée pour votre demande le ${formattedDate}`,
        details: {
          interventionDate: interventionDate,
          requestTitle: workRequest.title || `Demande #${workRequest.id.substring(0, 8)}`,
          location: `${workRequest.location?.building || ''} ${workRequest.location?.room || ''}`
        }
      });

      const result = await addDoc(collection(db, 'notifications'), {
        ...requesterNotification,
        createdAt: serverTimestamp()
      });

      return result;
    } catch (error) {
      console.error('Erreur lors de la création de la notification d\'intervention:', error);
      throw error;
    }
  },

  // Créer une notification de rappel d'intervention
  async createInterventionReminderNotification(workRequest, interventionDate) {
    try {
      if (!workRequest.id || !workRequest.createdBy || !interventionDate) {
        throw new Error('Informations manquantes pour le rappel d\'intervention');
      }

      const formattedDate = new Date(interventionDate).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      const requesterNotification = createNotification({
        type: NOTIFICATION_TYPES.INTERVENTION_REMINDER,
        workRequestId: workRequest.id,
        serviceId: workRequest.createdBy,
        message: `Rappel: Intervention prévue demain à ${new Date(interventionDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
        details: {
          interventionDate: interventionDate,
          requestTitle: workRequest.title || `Demande #${workRequest.id.substring(0, 8)}`,
          location: `${workRequest.location?.building || ''} ${workRequest.location?.room || ''}`
        }
      });

      const result = await addDoc(collection(db, 'notifications'), {
        ...requesterNotification,
        createdAt: serverTimestamp()
      });

      return result;
    } catch (error) {
      console.error('Erreur lors de la création du rappel d\'intervention:', error);
      throw error;
    }
  },

  // Créer une notification de demande de feedback
  async createFeedbackRequestNotification(workRequest) {
    try {
      if (!workRequest.id || !workRequest.createdBy) {
        throw new Error('Informations manquantes pour la demande de feedback');
      }

      const requesterNotification = createNotification({
        type: NOTIFICATION_TYPES.FEEDBACK_REQUESTED,
        workRequestId: workRequest.id,
        serviceId: workRequest.createdBy,
        message: `Merci de donner votre avis sur l'intervention réalisée`,
        details: {
          requestTitle: workRequest.title || `Demande #${workRequest.id.substring(0, 8)}`,
          completedDate: workRequest.completedAt || new Date()
        }
      });

      const result = await addDoc(collection(db, 'notifications'), {
        ...requesterNotification,
        createdAt: serverTimestamp()
      });

      return result;
    } catch (error) {
      console.error('Erreur lors de la création de la demande de feedback:', error);
      throw error;
    }
  },

  // Récupérer les notifications pour un utilisateur spécifique (pas seulement un service)
  async getUserNotifications(userId) {
    try {
      if (!userId) {
        console.warn('UserId manquant pour la récupération des notifications');
        return [];
      }

      const notificationsRef = collection(db, 'notifications');
      const notificationsQuery = query(
        notificationsRef,
        where('serviceId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(notificationsQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications utilisateur:', error);
      return [];
    }
  },
  /**
   * Met à jour toutes les notifications non lues d'un utilisateur comme lues
   * @param {string} userId - ID de l'utilisateur
   */
  async markAllAsRead(userId) {
    if (!userId) return;

    try {
      const q = query(
        collection(db, 'notifications'),
        where('serviceId', '==', userId),
        where('status', '==', NOTIFICATION_STATUS.UNREAD)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) return;

      // writeBatch limite à 500 docs — suffisant pour les notifications d'un utilisateur
      const batch = writeBatch(db);
      snapshot.docs.forEach((notifDoc) => {
        batch.update(notifDoc.ref, {
          status: NOTIFICATION_STATUS.READ,
          readAt: new Date(),
          updatedAt: new Date(),
        });
      });

      await batch.commit();
    } catch (error) {
      console.error('Erreur lors de la mise à jour des notifications:', error);
    }
  },

  /**
   * Crée une notification pour informer qu'un feedback a été laissé sur une demande
   * @param {Object} workRequest - La demande de travaux
   * @param {number} rating - La note donnée (1-5)
   * @param {string} requesterName - Nom du demandeur qui a laissé le feedback
   * @param {string} technicalServiceId - ID du service technique à notifier
   * @returns {Promise<string>} - ID de la notification créée
   */
  async createFeedbackNotification(workRequest, rating, requesterName, technicalServiceId) {
    try {
      if (!workRequest || !technicalServiceId) {
        console.warn('Données manquantes pour la création de la notification de feedback');
        return null;
      }

      const notificationData = {
        type: NOTIFICATION_TYPES.FEEDBACK_RECEIVED,
        title: `Nouveau feedback reçu`,
        message: `${requesterName} a évalué votre intervention avec ${rating} étoile${rating > 1 ? 's' : ''}.`,
        status: NOTIFICATION_STATUS.UNREAD,
        serviceId: technicalServiceId,
        workRequestId: workRequest.id,
        workRequestTitle: workRequest.title || workRequest.description?.substring(0, 30) || 'Demande de travaux',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'notifications'), notificationData);
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de la création de la notification de feedback:', error);
      return null;
    }
  },

  /**
   * Crée une notification pour informer un service technique d'une nouvelle demande de travaux
   * @param {Object} workRequest - La demande de travaux
   * @param {Object} technicalService - Le service technique à notifier
   * @returns {Promise<string>} - ID de la notification créée
   */
  async createWorkRequestNotification(workRequest, technicalService) {
    try {
      if (!workRequest || !workRequest.id || !technicalService || !technicalService.id) {
        console.warn('Données manquantes pour la création de la notification de demande de travaux');
        return null;
      }

      const problemTranslation = extractProblemLabel(workRequest);
      const { building: buildingTranslation, room: roomValue } = extractLocation(workRequest);

      const notificationData = createNotification({
        type: NOTIFICATION_TYPES.NEW_REQUEST,
        workRequestId: workRequest.id,
        serviceId: technicalService.id,
        message: `Nouvelle demande de travaux : ${problemTranslation}`,
        details: {
          problem: problemTranslation,
          building: buildingTranslation || null,
          room: roomValue || null,
          priority: workRequest.priority || null,
          title: workRequest.title || null
        }
      });

      const docRef = await addDoc(collection(db, 'notifications'), {
        ...notificationData,
        createdAt: serverTimestamp()
      });

      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de la création de la notification de demande de travaux:', error);
      return null;
    }
  },

  /**
   * Réinitialise toutes les notifications dans Firestore
   * Cette fonction est utilisée pour nettoyer les notifications factices ou incorrectes
   * @returns {Promise<void>}
   */
  async resetAllNotifications(userId) {
    try {
      if (!userId) throw new Error('userId requis pour réinitialiser les notifications');

      const snapshot = await getDocs(
        query(collection(db, 'notifications'), where('serviceId', '==', userId))
      );
      if (snapshot.empty) return;

      // Suppression par lots de 500 (limite Firestore writeBatch)
      const chunks = [];
      for (let i = 0; i < snapshot.docs.length; i += 500) {
        chunks.push(snapshot.docs.slice(i, i + 500));
      }

      await Promise.all(
        chunks.map(async (chunk) => {
          const batch = writeBatch(db);
          chunk.forEach((notifDoc) => batch.delete(notifDoc.ref));
          return batch.commit();
        })
      );
    } catch (error) {
      console.error('NotificationService: Erreur lors de la réinitialisation des notifications:', error);
      throw error;
    }
  }
};
